// Full appointment editor, opened by double-clicking an appointment card.
// Follows the SlotFormSheet skeleton (right-side sheet, header / scrolling
// form / footer). Fields that back-sync to Plato carry a PlatoSyncBadge —
// see plato-sync.jsx for the contract. Saves go through the calendar's
// shared applyAppointmentUpdate path (optimistic + undo toast), so a save,
// a drag-move and an undo are all the same kind of commit.

import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { Building2, CalendarDays, CalendarX2, Check, MapPin, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { isDirectEntryEvent } from "@/lib/calendars";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { DatePicker } from "@/components/ui/date-picker";
import { fetchTenants } from "@/lib/api/tenants";
import { fetchDoctors } from "@/lib/api/users";
import { resolveTenantDisplayName } from "@/lib/tenant-display";
import {
  NIL_PATIENT_ID,
  buildEventColorChoices,
  isCancelledCalendarStatus,
  newPatientPhoneFromEvent,
} from "../calendar-utils";
import { MIN_APPOINTMENT_MINUTES, snapMinutes } from "../calendar-drag-geometry";
import { getCalendarById } from "../calendar-data";
import { useCalendarMutations } from "../calendar-mutations";
import { LocalOnlyBadge, PlatoSyncBadge } from "../plato-sync";
import { AppointmentPatientPanel } from "./appointment-patient-panel";
import { AppointmentHistoryPanel } from "./appointment-history-panel";
import {
  ColorSwatch,
  ComboboxField,
  FieldLabel,
  TimePicker,
  dateToYmd,
  isValidHhmm,
  toDoctorOption,
  toTenantOption,
  ymdToDate,
} from "./form-fields";

// Editor-save changes that a patient would care about — a save touching any of
// these offers a fresh confirmation. Cosmetic keys (colorHexOverride) are absent.
const CONFIRMATION_RELEVANT_KEYS = ["startAt", "endAt", "doctorId", "tenantId", "title", "description"];

function formStateFromEvent(event) {
  return {
    title: String(event?.title || ""),
    description: String(event?.description || ""),
    date: event?.startAt?.toISODate?.() || "",
    startTime: event?.startAt?.toFormat?.("HH:mm") || "",
    endTime: event?.endAt?.toFormat?.("HH:mm") || "",
    doctorId: String(event?.doctorId || ""),
    tenantId: String(event?.tenantId || ""),
    // The explicit per-appointment color override; "" = automatic (follow the
    // calendar color).
    colorHex: String(event?.colorHexOverride || ""),
  };
}

// Field label with the Plato write-back marker. `synced={false}` (local-only
// calendars, whose entries are never pushed to Plato) renders the bare label.
function SyncedFieldLabel({ field, synced = true, children }) {
  if (!synced) {
    return <FieldLabel>{children}</FieldLabel>;
  }
  return (
    <FieldLabel>
      <span className="inline-flex items-center gap-1.5">
        {children}
        <PlatoSyncBadge field={field} />
      </span>
    </FieldLabel>
  );
}

export function AppointmentFormSheet({ open, onOpenChange, event, use12HourFormat = false }) {
  const mutations = useCalendarMutations();
  const [form, setForm] = useState(() => formStateFromEvent(event));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Doctor and clinic rosters, fetched lazily when the sheet opens (same
  // pattern as SlotFormSheet). Until they load — or if the fetch fails — the
  // comboboxes fall back to the event's current doctor/clinic as their only
  // option, so the current value always displays.
  const [fetchedDoctorOptions, setFetchedDoctorOptions] = useState([]);
  const [fetchedTenantOptions, setFetchedTenantOptions] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  // The appointment's calendar (from the cached calendars index): decides whether
  // this is a standalone-calendar entry (no doctor/clinic to edit) and whether the
  // calendar is local-only (nothing syncs to Plato, so no sync badges).
  const [calendarInfo, setCalendarInfo] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const calendarId = String(event?.calendarId || "").trim();
    if (!calendarId) {
      setCalendarInfo(null);
      return undefined;
    }
    let cancelled = false;
    getCalendarById(calendarId)
      .then((info) => {
        if (!cancelled) setCalendarInfo(info || null);
      })
      .catch(() => {
        if (!cancelled) setCalendarInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, event?.calendarId]);

  const standalone = isDirectEntryEvent(event) || Boolean(calendarInfo && !calendarInfo.doctorId);
  const localOnly = Boolean(calendarInfo?.localOnly);
  const calendarName = String(calendarInfo?.name || event?.calendarName || "").trim();

  useEffect(() => {
    if (!open) return;
    setDoctorsLoading(true);
    fetchDoctors()
      .then((res) => {
        const items = Array.isArray(res?.items) ? res.items : [];
        setFetchedDoctorOptions(items.map(toDoctorOption).filter(Boolean));
      })
      .catch(() => {})
      .finally(() => setDoctorsLoading(false));
    setTenantsLoading(true);
    fetchTenants()
      .then((res) => {
        const items = Array.isArray(res?.items) ? res.items : [];
        setFetchedTenantOptions(items.map(toTenantOption).filter(Boolean));
      })
      .catch(() => {})
      .finally(() => setTenantsLoading(false));
  }, [open]);

  const doctorOptions = useMemo(() => {
    const current = String(event?.doctorId || "").trim();
    if (!current || fetchedDoctorOptions.some((option) => option.value === current)) {
      return fetchedDoctorOptions;
    }
    return [
      { value: current, label: String(event?.doctor || "").trim() || current },
      ...fetchedDoctorOptions,
    ];
  }, [fetchedDoctorOptions, event?.doctorId, event?.doctor]);

  const tenantOptions = useMemo(() => {
    const current = String(event?.tenantId || "").trim();
    if (!current || fetchedTenantOptions.some((option) => option.value === current)) {
      return fetchedTenantOptions;
    }
    return [
      { value: current, label: resolveTenantDisplayName(event?._tenant, current) },
      ...fetchedTenantOptions,
    ];
  }, [fetchedTenantOptions, event?.tenantId, event?._tenant]);

  const cancelled = isCancelledCalendarStatus(event?.status);

  // Color choices: five complementary colors around the wheel, anchored at
  // the appointment's inherited base hue (the calendar color, falling back to
  // the current override / emerald default). A saved override outside the
  // generated set stays visible as its own leading swatch, so the current
  // color is always selectable.
  const colorChoices = useMemo(() => {
    const base = String(event?.colorHexBase || event?.colorHexOverride || "").trim();
    const accents = buildEventColorChoices(base || undefined);
    const override = String(event?.colorHexOverride || "").trim().toUpperCase();
    if (override && !accents.includes(override)) {
      return [override, ...accents];
    }
    return accents;
  }, [event?.colorHexBase, event?.colorHexOverride]);

  useEffect(() => {
    if (open) {
      setForm(formStateFromEvent(event));
      setSubmitError("");
    }
    // Re-derive only when the sheet (re)opens — mid-edit realtime updates must
    // not clobber the user's draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const durationMinutes = useMemo(() => {
    if (!isValidHhmm(form.startTime) || !isValidHhmm(form.endTime) || !form.date) return null;
    const start = DateTime.fromISO(`${form.date}T${form.startTime}`);
    const end = DateTime.fromISO(`${form.date}T${form.endTime}`);
    if (!start.isValid || !end.isValid) return null;
    return Math.round(end.diff(start, "minutes").minutes);
  }, [form.date, form.startTime, form.endTime]);

  const buildChanges = () => {
    if (!form.date || !/^\d{4}-\d{2}-\d{2}$/.test(form.date)) {
      return { error: "Pick a valid date." };
    }
    if (!isValidHhmm(form.startTime) || !isValidHhmm(form.endTime)) {
      return { error: "Times must be HH:MM." };
    }
    // Events are displayed (and dragged) in the local zone, so the editor
    // builds the new instants there too; the API payload goes out as UTC.
    let startAt = DateTime.fromISO(`${form.date}T${form.startTime}`);
    let endAt = DateTime.fromISO(`${form.date}T${form.endTime}`);
    if (!startAt.isValid || !endAt.isValid) {
      return { error: "Invalid date or time." };
    }
    const snappedStart = snapMinutes(startAt.hour * 60 + startAt.minute);
    const snappedEnd = snapMinutes(endAt.hour * 60 + endAt.minute);
    startAt = startAt.set({ hour: Math.floor(snappedStart / 60), minute: snappedStart % 60, second: 0, millisecond: 0 });
    endAt = endAt.set({ hour: Math.floor(snappedEnd / 60), minute: snappedEnd % 60, second: 0, millisecond: 0 });
    if (endAt.diff(startAt, "minutes").minutes < MIN_APPOINTMENT_MINUTES) {
      return { error: `Appointments need at least ${MIN_APPOINTMENT_MINUTES} minutes.` };
    }

    const changes = {};
    if (form.title !== String(event?.title || "")) changes.title = form.title.trim();
    if (form.description !== String(event?.description || "")) {
      changes.description = form.description;
    }
    if (
      startAt.toMillis() !== event.startAt.toMillis() ||
      endAt.toMillis() !== event.endAt.toMillis()
    ) {
      changes.startAt = startAt;
      changes.endAt = endAt;
    }
    if (form.doctorId && form.doctorId !== String(event?.doctorId || "")) {
      changes.doctorId = form.doctorId;
      // Display name rides along for the optimistic apply only (same as the
      // drag path) — it is not part of the API payload.
      const doctorName = doctorOptions.find((option) => option.value === form.doctorId)?.label;
      if (doctorName) changes.doctor = doctorName;
    }
    if (form.tenantId && form.tenantId !== String(event?.tenantId || "")) {
      changes.tenantId = form.tenantId;
    }
    if (form.colorHex !== String(event?.colorHexOverride || "")) {
      // colorHexOverride is the persisted field ("" clears it); the resolved
      // display color rides along for the optimistic apply only.
      changes.colorHexOverride = form.colorHex;
      changes.colorHex = form.colorHex || event?.colorHexBase || "";
    }
    return { changes };
  };

  const handleSave = async () => {
    if (!mutations?.applyAppointmentUpdate || submitting) return;
    const { changes, error } = buildChanges();
    if (error) {
      setSubmitError(error);
      return;
    }
    if (!changes || Object.keys(changes).length === 0) {
      onOpenChange(false);
      return;
    }
    setSubmitError("");
    setSubmitting(true);
    const ok = await mutations.applyAppointmentUpdate({
      event,
      changes,
      toastLabel: "Appointment updated",
    });
    setSubmitting(false);
    if (ok) {
      onOpenChange(false);
      maybeOfferConfirmation(changes);
    }
  };

  // After saving patient-relevant detail edits (when/who/where/title/notes),
  // offer to send a fresh confirmation. A real patient messages their linked
  // user; a new-patient booking messages by the phone on the appointment.
  // Cosmetic-only saves (color) and bookings with no way to message don't prompt.
  // Standalone-calendar entries are not visits, so they never prompt either.
  const maybeOfferConfirmation = (changes) => {
    if (standalone) return;
    if (!CONFIRMATION_RELEVANT_KEYS.some((key) => changes[key] !== undefined)) return;
    const patientId = String(event?.patientId || "").trim();
    const hasRealPatient = Boolean(patientId) && patientId !== NIL_PATIENT_ID;
    const newPhone = newPatientPhoneFromEvent(event);
    if (!hasRealPatient && !newPhone) return;
    mutations?.requestAppointmentConfirmation?.({
      appointmentId: event.id,
      patientUserId: patientId,
      patientName: String(event.patient || event.title || "").trim(),
      phone: hasRealPatient ? String(event._patient?.phone || "").trim() : newPhone,
    });
  };

  const handleStatusChange = async (nextStatus) => {
    if (!mutations?.applyAppointmentUpdate || submitting) return;
    setSubmitting(true);
    const ok = await mutations.applyAppointmentUpdate({
      event,
      changes: { status: nextStatus },
      toastLabel: nextStatus === "cancelled" ? "Appointment cancelled" : "Appointment restored",
    });
    setSubmitting(false);
    if (ok) onOpenChange(false);
  };

  const timeHint =
    durationMinutes != null && durationMinutes > 0
      ? `${durationMinutes} min`
      : null;

  // Clinic + location line for the header. `event.location` already falls back
  // to the tenant name when the raw location is empty (see calendar-data.js),
  // so an identical value is dropped rather than shown twice.
  const clinicName = resolveTenantDisplayName(event?._tenant);
  const locationLabel = (() => {
    const location = String(event?.location || "").trim();
    return location && location !== clinicName ? location : "";
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="h-full max-h-dvh w-full gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="relative z-10 shrink-0 border-b border-border bg-background px-6 pb-4 pl-6 pr-14 pt-5 text-left">
          <SheetTitle>{standalone ? "Edit entry" : "Edit appointment"}</SheetTitle>
          <SheetDescription>
            {event?.patient ? `${event.patient} · ` : ""}
            {event?.startAt?.toFormat?.("ccc d LLL yyyy") || ""}
            {cancelled ? " · cancelled" : ""}
            {standalone && calendarName ? (
              <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-foreground/85">
                <span className="inline-flex min-w-0 items-center gap-1">
                  <CalendarDays className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="truncate">Calendar: {calendarName}</span>
                </span>
              </span>
            ) : null}
            {!standalone && (clinicName || locationLabel) ? (
              <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-foreground/85">
                {clinicName ? (
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <Building2 className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="truncate">{clinicName}</span>
                  </span>
                ) : null}
                {locationLabel ? (
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <MapPin className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="truncate">{locationLabel}</span>
                  </span>
                ) : null}
              </span>
            ) : null}
            {localOnly ? (
              <span className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                <LocalOnlyBadge />
                Local only — changes stay in Healo.
              </span>
            ) : (
              <span className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                <PlatoSyncBadge field="status" className="inline-flex items-center text-muted-foreground/70" />
                Marked fields sync back to Plato on save.
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="min-h-0 flex-1">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="space-y-5 px-6 py-5"
          >
            <AppointmentPatientPanel event={event} open={open} />

            <AppointmentHistoryPanel event={event} open={open} />

            <div className="space-y-1.5">
              <SyncedFieldLabel field="title" synced={!localOnly}>Title</SyncedFieldLabel>
              <Input
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Patient name / appointment title"
                disabled={cancelled}
              />
            </div>

            <div className="space-y-1.5">
              <SyncedFieldLabel field="date" synced={!localOnly}>Date</SyncedFieldLabel>
              <DatePicker
                date={ymdToDate(form.date)}
                setDate={(next) => setForm((prev) => ({ ...prev, date: dateToYmd(next) }))}
                disabled={cancelled}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <SyncedFieldLabel field="startTime" synced={!localOnly}>Start</SyncedFieldLabel>
                <TimePicker
                  value={form.startTime}
                  onChange={(hhmm) => setForm((prev) => ({ ...prev, startTime: hhmm }))}
                />
              </div>
              <div className="space-y-1.5">
                <SyncedFieldLabel field="endTime" synced={!localOnly}>End</SyncedFieldLabel>
                <TimePicker
                  value={form.endTime}
                  onChange={(hhmm) => setForm((prev) => ({ ...prev, endTime: hhmm }))}
                />
              </div>
            </div>
            {timeHint ? (
              <p className="-mt-3 text-xs text-muted-foreground">Duration: {timeHint}</p>
            ) : null}

            {/* Standalone-calendar entries have no doctor or clinic to reassign — the
                calendar itself is named in the header instead. */}
            {standalone ? null : (
              <>
                <ComboboxField
                  label="Doctor"
                  labelSuffix={localOnly ? null : <PlatoSyncBadge field="doctor" />}
                  value={form.doctorId}
                  options={doctorOptions}
                  onChange={(doctorId) => setForm((prev) => ({ ...prev, doctorId }))}
                  loading={doctorsLoading}
                  disabled={cancelled}
                  placeholder="No doctor"
                />

                <ComboboxField
                  label="Clinic"
                  value={form.tenantId}
                  options={tenantOptions}
                  onChange={(tenantId) => setForm((prev) => ({ ...prev, tenantId }))}
                  loading={tenantsLoading}
                  disabled={cancelled}
                  placeholder="No clinic"
                />
              </>
            )}

            <div className="space-y-1.5">
              <FieldLabel>Color</FieldLabel>
              <div className="flex flex-wrap items-center gap-2 pt-0.5">
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, colorHex: "" }))}
                  title="Automatic — follow the calendar color"
                  aria-pressed={form.colorHex === ""}
                  disabled={cancelled}
                  className={cn(
                    "relative flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full border border-dashed border-foreground/35 shadow-sm transition-transform hover:scale-110 disabled:pointer-events-none disabled:opacity-50",
                    form.colorHex === "" && "ring-2 ring-primary ring-offset-2 ring-offset-background",
                  )}
                  style={event?.colorHexBase ? { backgroundColor: event.colorHexBase } : undefined}
                >
                  {form.colorHex === "" ? (
                    <Check
                      className="size-3.5 text-white drop-shadow-[0_1px_1px_rgb(0_0_0/0.6)]"
                      strokeWidth={3}
                      aria-hidden
                    />
                  ) : (
                    <span
                      className="absolute inset-x-0.5 top-1/2 h-px -rotate-45 bg-foreground/35"
                      aria-hidden
                    />
                  )}
                </button>
                {colorChoices.map((hex) => (
                  <ColorSwatch
                    key={hex}
                    hex={hex}
                    selected={form.colorHex === hex}
                    onSelect={() => setForm((prev) => ({ ...prev, colorHex: hex }))}
                    disabled={cancelled}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Accent for this appointment&apos;s card only — the dashed swatch follows the
                calendar&apos;s own color.
              </p>
            </div>

            <div className="space-y-1.5">
              <SyncedFieldLabel field="description" synced={!localOnly}>Notes</SyncedFieldLabel>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Staff notes / instructions"
                rows={5}
                disabled={cancelled}
              />
            </div>

            {submitError ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {submitError}
              </div>
            ) : null}
          </form>
        </ScrollArea>

        <div className="shrink-0 border-t border-border bg-background px-6 py-4">
          <div className="flex items-center justify-between gap-2">
            {cancelled ? (
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                onClick={() => handleStatusChange("scheduled")}
              >
                <RotateCcw className="mr-2 size-4" aria-hidden />
                Restore appointment
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={submitting}
                onClick={() => handleStatusChange("cancelled")}
              >
                <CalendarX2 className="mr-2 size-4" aria-hidden />
                {standalone ? "Cancel entry" : "Cancel appointment"}
              </Button>
            )}
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Close
              </Button>
              <Button type="button" onClick={handleSave} disabled={submitting || cancelled} aria-busy={submitting}>
                {submitting ? <Spinner className="mr-2 size-4" /> : null}
                Save changes
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
